import Link from "next/link";

// Ribbon background: 40px-wide FLAT ribbon with darker vertical bars
// down the left and right edges, matching the flat red ribbon band in
// the site logo (upper-left). The center holds one flat mid-red
// (#EE4545); each edge deepens to the darkest red (#9F2424) so the
// ribbon reads as lying flat with shadowed folds at its edges — not a
// cylinder lit from one side. The gradient is symmetric (dark → flat →
// dark), same red palette as before. The capping bow lives on the
// eCards banner below; see EcardsNarrativeBanner's FlowerBow.
//
// The dark edge bars are kept NARROW (folds at ~0–14% / 86–100%) so the
// vivid mid-red spans most of the 40px and the ribbon reads full-width.
// Earlier wider bars (20–80% center) made the flat ribbon look thinner
// than the old directional gradient it replaced.
//
// Exported so EcardsNarrativeBanner can reuse the exact same gradient
// — keeping the two banners visually identical without drift.
export const RIBBON_BACKGROUND =
  "linear-gradient(to right, #9F2424 0%, #C73838 5%, #EE4545 14%, #EE4545 86%, #C73838 95%, #9F2424 100%)";

// A tight-weave fabric texture laid over the ribbon so it reads as
// woven grosgrain rather than a flat paint fill. Two very-low-opacity
// repeating diagonal gradients cross at ±45° — light threads one way,
// faint shadow the other — tiling a 4px weave. Kept subtle enough to be
// felt more than seen; it's texture, not a pattern. A separate layer so
// the flat base and its intent tests stay untouched.
export const RIBBON_WEAVE =
  "repeating-linear-gradient(45deg, rgba(255,255,255,0.05) 0px, rgba(255,255,255,0.05) 1px, transparent 1px, transparent 4px), " +
  "repeating-linear-gradient(-45deg, rgba(0,0,0,0.06) 0px, rgba(0,0,0,0.06) 1px, transparent 1px, transparent 4px)";

// The ribbon surface every banner paints: the woven texture on top of
// the flat base. CSS paints the first-listed background layer on top.
export const RIBBON_SURFACE = `${RIBBON_WEAVE}, ${RIBBON_BACKGROUND}`;

// Seasonal banner shown above the search bar on `/` and on the
// holiday landing pages. Drives the user toward the active holiday's
// dedicated landing page
// with a visible internal link — also closes the SEO "orphaned page"
// finding for whichever holiday is active.
//
// Add a new holiday by appending to HOLIDAY_BANNERS. The first banner
// whose date window contains the current date wins. Outside every
// window, the component returns null and the page renders without a
// banner.
//
// Note on freshness: this is a Server Component, so the date check
// runs at render time on the server. For static/cached pages, that
// can be deploy time. Since holiday windows are days-wide and deploys
// happen well within that, day-level accuracy is fine — the worst
// case is a banner that stays up an extra few hours after a holiday.

type HolidayBanner = {
  href: string;
  headline: string;
  subhead: string;
  cta: string;
  // Inclusive window in YYYY-MM-DD format (local time of the
  // rendering machine). endDate is treated as end-of-day.
  startDate: string;
  endDate: string;
  // Optional Tailwind background class for the banner section. Lets
  // each holiday carry its own thematic color (e.g. flag-navy for
  // Independence Day) without editing the component. Falls back to
  // brand purple when omitted.
  bgClassName?: string;
};

// Keep past and future holidays in this list — outside every window
// the component returns null. Activation/expiration only takes effect
// on the next deploy (or when the page's cache is invalidated): see
// the freshness note above. Annual maintenance is just rolling the
// YYYY forward.
const HOLIDAY_BANNERS: ReadonlyArray<HolidayBanner> = [
  {
    href: "/fathersday",
    headline: "Father's Day is Sunday, June 21",
    subhead:
      "Send an animated card from real artists — arrives in seconds, no shipping window to miss.",
    cta: "Browse Father's Day Cards",
    startDate: "2026-06-07",
    endDate: "2026-06-22",
  },
  {
    // No dedicated /independenceday landing page yet — link to the
    // home page with the existing Independence Day category filter
    // pre-selected (see SearchBar's `?category=` handling).
    href: "/?category=Independence%20Day",
    headline: "Independence Day is Saturday, July 4",
    subhead:
      "Send an animated card from real artists — arrives in seconds, no shipping window to miss.",
    cta: "Browse Independence Day Cards",
    startDate: "2026-06-28",
    endDate: "2026-07-05",
    bgClassName: "bg-blue-900",
  },
];

// Parse "YYYY-MM-DD" into a local-time Date at the start of that day.
// `new Date("YYYY-MM-DD")` is interpreted as UTC, which shifts into the
// previous day in any negative-offset timezone — not what we want for
// human-meaningful holiday windows.
function parseLocalDate(yyyyMmDd: string): Date {
  const [year, month, day] = yyyyMmDd.split("-").map(Number);
  return new Date(year ?? 0, (month ?? 1) - 1, day ?? 1);
}

export function getActiveHolidayBanner(
  now: Date = new Date(),
  banners: ReadonlyArray<HolidayBanner> = HOLIDAY_BANNERS,
): HolidayBanner | null {
  for (const banner of banners) {
    const start = parseLocalDate(banner.startDate);
    const end = parseLocalDate(banner.endDate);
    end.setHours(23, 59, 59, 999);
    if (now >= start && now <= end) return banner;
  }
  return null;
}

export default function SeasonalLandingBanner() {
  const banner = getActiveHolidayBanner();
  if (!banner) return null;

  return (
    <section
      aria-label="Seasonal highlight"
      // `relative z-10` + soft drop shadow give a clear horizontal
      // seam against the next section (matters on `/`, where the
      // section below is also bg-wg-purple). z-10 is enough because
      // nothing in this banner overhangs into the sticky navbar — the
      // capping bow lives on the eCards narrative banner below.
      className={`${banner.bgClassName ?? "bg-wg-purple"} relative z-10 text-white shadow-[0_4px_12px_rgba(0,0,0,0.45)]`}
    >
      {/* Inner container uses pl-16 (64px) to leave space for the
          40px-wide ribbon + 24px content padding. The ribbon is
          absolutely positioned within this relative container. */}
      <div className="relative mx-auto flex max-w-6xl flex-col items-start gap-3 py-4 pl-16 pr-6 sm:flex-row sm:items-center sm:justify-between">
        {/* Ribbon: 40px wide, flat symmetric red band (RIBBON_BACKGROUND)
            with a subtle woven-fabric texture (RIBBON_WEAVE) over it —
            see RIBBON_SURFACE. */}
        <div
          aria-hidden="true"
          className="pointer-events-none absolute bottom-0 left-0 top-0 w-[40px]"
          style={{ background: RIBBON_SURFACE }}
        />
        <div>
          <p className="text-lg font-bold md:text-xl">{banner.headline}</p>
          <p className="text-sm text-white/90">{banner.subhead}</p>
        </div>
        <Link
          href={banner.href}
          className="text-wg-purple inline-block whitespace-nowrap rounded-lg bg-white px-5 py-2 text-sm font-semibold transition-colors hover:bg-gray-100 md:text-base"
        >
          {banner.cta} →
        </Link>
      </div>
    </section>
  );
}
